import React, {
  FunctionComponent,
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react'
import Modal from 'react-bootstrap/Modal'
import { tt } from '../../../i18n'
import WheelButton from '../../../components/cta/WheelButton'
import UserDTO from '@tootsweet/model/datatransfer/UserDTO'
import Signature from '@tootsweet/model/lottery/Signature'
import LotteryGameConsume from '@tootsweet/model/lottery/LotteryGameConsume'
import TSConstants from '../../../utils/TSConstants'
import { TSUtils } from '../../../utils/TSUtils'
import { Loader } from '../../../components/Loader/Loader'
const SignatureCanvas = lazy(() => import('react-signature-canvas'))

interface Props {
  show: boolean
  hide: () => void
  user?: UserDTO | null
  game?: LotteryGameConsume | null
  completeForm: (signature: Signature) => void
  percentCompleted: number | undefined | null
  sign: (blob: Blob) => void
  gdprLegalNotice?: string
}

const STEP_FORM = 0
const STEP_SIGN = 2

const SIGN_WIDTH = 246
const SIGN_HEIGHT = 113

const SigningModal: FunctionComponent<Props> = (props) => {
  const [step, setStep] = useState(-1)
  const [firstName, setFirstName] = useState(
    props.game?.signature?.firstName || props.user?.firstName || ''
  )
  const [email, setEmail] = useState(
    props.game?.signature?.email || props.user?.paymentEmail || ''
  )
  const [lastName, setLastName] = useState(
    props.game?.signature?.lastName || props.user?.lastName || ''
  )
  const [phoneNumber, setPhoneNumber] = useState(
    props.game?.signature?.phoneNumber || props.user?.phone || ''
  )
  const [address, setAddress] = useState(props.game?.signature?.address || '')
  const [postalCode, setPostalCode] = useState(
    props.game?.signature?.postalCode || ''
  )
  const [city, setCity] = useState(props.game?.signature?.city || '')
  const [civility, setCivility] = useState(
    props.game?.signature?.civility || ''
  )
  const [didSign, setDidSign] = useState(false)
  const [isOptinSignature, setIsOptinSignature] = useState(false)

  const signRef = useRef<any | null>(null)

  function onBeginSigning() {
    setDidSign(true)
  }

  //brah
  function clear() {
    signRef?.current?.clear()
    setDidSign(false)
  }

  function sign() {
    if (!didSign) {
      alert(tt('signing_modal_not_signed_alert'))
      return
    }

    if (!isOptinSignature) {
      alert(tt('signing_modal_checkbox_required_alert'))
      return
    }

    const res = signRef.current?.toDataURL('image/png')

    if (!res) {
      alert(tt('error'))
      return
    }

    props.sign(TSUtils.dataURItoBlob(res))
  }

  async function onSubmit(e: any) {
    if (e && e.preventDefault) {
      e.preventDefault()
    }
    await props.completeForm({
      civility,
      firstName,
      lastName,
      phoneNumber,
      email,
      address,
      postalCode,
      city,
    })
    setStep(STEP_SIGN)
  }

  function renderForm() {
    return (
      <>
        <form onSubmit={onSubmit}>
          <div className="row w-margin-bottom">
            <div className="col-12">
              {tt('signing_modal_form_instruction')}
            </div>
          </div>
          <div className="row w-margin-bottom-half">
            <div className="col-12">
              <select
                id="civility"
                className="form-control"
                value={civility}
                onChange={(e) => setCivility(e.target.value)}
                required={true}
              >
                <option value="">{tt('placeholder_civility')}</option>
                <option value="Monsieur">{tt('signing_modal_civility_mr')}</option>
                <option value="Madame">{tt('signing_modal_civility_mrs')}</option>
                <option value="Mademoiselle">{tt('signing_modal_civility_miss')}</option>
              </select>
            </div>
          </div>
          <div className="row w-margin-bottom-half">
            <div className="col-12">
              <input
                id="firstName"
                type="text"
                placeholder={tt('placeholder_first_name')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="form-control"
                required={true}
              />
            </div>
          </div>
          <div className="row w-margin-bottom-half">
            <div className="col-12">
              <input
                id="lastName"
                type="text"
                placeholder={tt('placeholder_last_name')}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="form-control"
                required={true}
              />
            </div>
          </div>
          <div className="row w-margin-bottom-half">
            <div className="col-12">
              <input
                id="phoneNumber"
                className="form-control"
                placeholder={tt('placeholder_phone_number')}
                type="text"
                inputMode="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required={true}
              />
            </div>
          </div>
          <div className="row w-margin-bottom-half">
            <div className="col-12">
              <input
                id="email"
                type="email"
                placeholder={tt('placeholder_email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-control"
                required={true}
              />
            </div>
          </div>
          <div className="row w-margin-bottom-half">
            <div className="col-12">
              <input
                type="text"
                className="form-control"
                placeholder={tt('placeholder_address')}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required={true}
              />
            </div>
          </div>
          <div className="row w-margin-bottom-half">
            <div className="col-12">
              <input
                id="postalCode"
                type="text"
                placeholder={tt('placeholder_postcode')}
                value={postalCode}
                inputMode="numeric"
                onChange={(e) => setPostalCode(e.target.value)}
                className="form-control"
                required={true}
              />
            </div>
          </div>
          <div className="row w-margin-bottom-half">
            <div className="col-12">
              <input
                id="city"
                type="text"
                placeholder={tt('placeholder_city')}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="form-control"
                required={true}
              />
            </div>
          </div>
          <div className="row w-margin-bottom-half">
            <div className="col-12">
              <WheelButton isSubmit={true} text={tt('signing_modal_generate_certificate_button')} />
            </div>
          </div>
          <div className="row w-margin-bottom">
            {!!props.gdprLegalNotice && (
              <p className="text-justify" style={{ fontSize: 10 }}>
                {props.gdprLegalNotice}
              </p>
            )}
          </div>
        </form>
      </>
    )
  }

  function renderSigning() {
    return (
      <>
        <div className="row w-margin-bottom">
          <div className="col-12 text-center">
            <img
              className="twic"
              src={TSConstants.PLACEHOLDER_URL}
              data-src="image:/cdm/kadow/img/logo_h2o.png"
              style={{
                width: 200,
                height: 86,
              }}
            />
          </div>
        </div>
        <div className="row w-margin-bottom">
          <div className="col-12">
            <p style={{ textAlign: 'justify', fontSize: 14 }}>
              {tt('signing_modal_legal_representative_text')}
              <br />
              <br />
              {props.game?.signature?.civility}, {tt('signing_modal_participant_designation')}
              <br />
              <br />
              {tt('signing_modal_participant_info_prefix')} {props.game?.signature?.firstName}{' '}
              {props.game?.signature?.lastName?.toUpperCase()}
              <br />
              {tt('signing_modal_address_label')} {props.game?.signature?.address}
              <br />
              {tt('signing_modal_postal_code_label')} {props.game?.signature?.postalCode}
              <br />
              {tt('signing_modal_city_label')} {props.game?.signature?.city}
              <br />
              {tt('signing_modal_phone_label')} {props.game?.signature?.phoneNumber}
              <br />
              {tt('signing_modal_email_label')} {props.game?.signature?.email}
              <br />
              <br />
              <br />
              {tt('signing_modal_won_prize_text')}
              <br />
              <br />
              <b>{props.game?.prizeLongName}</b>
              <br />
              <br />
              {!!props.game?.conditions && (
                <>
                  <span
                    className="font-italic"
                    style={{ wordBreak: 'break-word' }}
                  >
                    {props.game?.conditions}
                  </span>
                  <br />
                  <br />
                </>
              )}
              {tt('signing_modal_game_description')}
              <br />
              <br />
              {tt('signing_modal_recorded_date')}
              <br />
              {tt('signing_modal_broadcast_date')}
              <br />
              <br />
              {tt('signing_modal_prize_acquisition_conditions')}
              <br />
              <br />
              {tt('signing_modal_participant_copy')}
              <br />
              <br />
              {tt('signing_modal_incorrect_info_warning')}
              <br />
              <br />
              {tt('signing_modal_h2o_no_liability_incorrect_info')}
              <br />
              <br />
              {tt('signing_modal_h2o_no_liability_additional_search')}
              <br />
              <br />
              {tt('signing_modal_h2o_no_liability_prize_damage')}
              <br />
              <br />
              {tt('signing_modal_h2o_no_liability_force_majeure')}
              <ul>
                <li>
                  {tt('signing_modal_force_majeure_event')}
                </li>
                <li>
                  {tt('signing_modal_third_party_fault')}
                </li>
                <li>
                  {tt('signing_modal_postal_delay_loss')}
                </li>
              </ul>
              <br />
              {tt('signing_modal_prize_acceptance_conditions')}
              <br />
              <br />
              {tt('signing_modal_electronic_signature_legal_text')}
              <br />
              <br />
            </p>
          </div>
        </div>
        <div className="row w-margin-bottom-third">
          <div className="col-12 text-center">
            <b>{tt('signing_modal_sign_instruction')}</b>
          </div>
        </div>
        <div className="row w-margin-bottom-half">
          <div className="col-12 d-flex justify-content-center">
            <Suspense
              fallback={
                <Loader
                  loading={true}
                  displayText={true}
                  text={tt('loading')}
                  isFixed={true}
                />
              }
            >
              <SignatureCanvas
                ref={signRef}
                penColor="black"
                onBegin={onBeginSigning}
                canvasProps={{
                  width: SIGN_WIDTH,
                  height: SIGN_HEIGHT,
                  className: 'sigCanvas',
                }}
              />
            </Suspense>
          </div>
        </div>
        <div className="row w-margin-bottom">
          <div className="col-12 d-flex justify-content-center">
            <span style={{ textDecoration: 'underline' }} onClick={clear}>
              {tt('signing_modal_clear_signature')}
            </span>
          </div>
        </div>
        <div className="row w-margin-bottom">
          <div className="col-12 text-center">
            <p>
              <div className="form-check">
                <input
                  id="optin-signature"
                  className="form-check-input"
                  type="checkbox"
                  checked={isOptinSignature}
                  onChange={(e) => setIsOptinSignature(e.target.checked)}
                  required={true}
                />
                <label
                  className="form-check-label"
                  htmlFor="optin-signature"
                  style={{ fontSize: 12 }}
                >
                  {tt('signing_modal_checkbox_label')}
                </label>
              </div>
            </p>
          </div>
        </div>
        <div className="row w-margin-bottom">
          <div className="col-12">
            <WheelButton text={tt('signing_modal_receive_certificate_button')} onClick={sign} />
          </div>
        </div>

        <div className="row w-margin-bottom">
          <div className="col-12 text-center">
            <p style={{ fontSize: 10 }}>
              {tt('signing_modal_company_info').split('\n').map((line, index) => (
                <span key={index}>
                  {line}
                  {index < tt('signing_modal_company_info').split('\n').length - 1 && <br />}
                </span>
              ))}
            </p>
          </div>
        </div>
      </>
    )
  }

  function setAppropriateStep() {
    if (formIsComplete(props)) {
      setStep(STEP_SIGN)
    } else {
      setStep(STEP_FORM)
    }
  }

  useEffect(() => {
    setAppropriateStep()
  }, [])

  useEffect(() => {
    setAppropriateStep()
  }, [props])

  return (
    <Modal
      show={props.show}
      centered={true}
      backdropClassName=""
      className="k-modal-signing"
    >
      <Modal.Body>
        <i className="fas fa-times modal-close" onClick={props.hide} />
        <div className="container">
          <div className="row w-margin-bottom">
            <div className="col-12">
              <h1>{tt('signing_modal_title')}</h1>
            </div>
          </div>
          {step === STEP_FORM && renderForm()}
          {step === STEP_SIGN && renderSigning()}
        </div>
      </Modal.Body>
    </Modal>
  )
}

function formIsComplete(props: Props) {
  return (
    !!props.game?.signature?.firstName &&
    props.game?.signature?.firstName.length > 0 &&
    !!props.game?.signature?.lastName &&
    props.game?.signature?.lastName.length > 0 &&
    !!props.game?.signature?.address &&
    props.game?.signature?.address.length > 0 &&
    !!props.game?.signature?.email &&
    props.game?.signature?.email.length > 0 &&
    !!props.game?.signature?.phoneNumber &&
    props.game?.signature?.phoneNumber.length > 0
  )
}

export default SigningModal
